/**
 * Turning an existing document into template content (BBD-109).
 *
 * - @mention blots become plain "@Name" text. A mention left in a template
 *   would re-notify that person every time a document is created from it
 *   (the editors collect `[data-user-uid]` on save to send notifications).
 * - #task references are kept as they are.
 * - Quill's embed guard characters (U+FEFF) are dropped.
 * - Viewer-only wrappers (scrollable table containers) are unwrapped so the
 *   template holds editor HTML, not display HTML.
 */
export function cleanContentForTemplate(html) {
    if (!html) return '';
    let doc;
    try {
        doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
        return html;
    }

    doc.querySelectorAll('span.user-mention, [data-user-uid]').forEach((el) => {
        const name = el.getAttribute('data-user-name') || el.textContent.replace(/﻿/g, '').trim();
        const text = name.startsWith('@') ? name : `@${name}`;
        el.replaceWith(doc.createTextNode(text));
    });

    doc.querySelectorAll('.Document__table-scroll, .table-scroll-container').forEach((wrapper) => {
        while (wrapper.firstChild) wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
        wrapper.remove();
    });

    return doc.body.innerHTML.replace(/﻿/g, '');
}

/** Lower-case key from a page title, unique within `taken`. */
export function keyFromTitle(title, taken) {
    let base = String(title || 'page').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'page';
    base = base.slice(0, 32);
    let key = base;
    let n = 2;
    while (taken.has(key)) {
        key = `${base}_${n}`;
        n += 1;
    }
    taken.add(key);
    return key;
}

/**
 * Build a kit's `pages` from a document's descendants as returned by the
 * viewers' fetchPersonalDescendants / fetchWorkspaceDescendants
 * ([{title, data}]). Parents are resolved by name (personal) or by id
 * (workspace, where `data.parent_page` is {id, name}).
 */
export function buildKitPages(descendants, rootName) {
    const taken = new Set();
    const keyByName = new Map();
    const keyById = new Map();
    const pages = [];
    (descendants || []).forEach(({ title, data }) => {
        const key = keyFromTitle(title, taken);
        keyByName.set(title, key);
        if (data?.id !== undefined) keyById.set(String(data.id), key);
        pages.push({ key, title, data });
    });
    return pages.map(({ key, title, data }) => {
        let parentKey = null;
        const parent = data?.parent_page;
        if (parent && typeof parent === 'object') {
            parentKey = keyById.get(String(parent.id)) || null;
        } else if (typeof parent === 'string' && parent !== rootName) {
            parentKey = keyByName.get(parent) || null;
        }
        return {
            key,
            name_pattern: title,
            content: cleanContentForTemplate(data?.content || ''),
            parent_key: parentKey,
        };
    });
}
